import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import axios from "axios";
import { toast } from "react-toastify";
import SelectSearchInput from "../SelectSearch";
import Select from "react-select";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

const AddWastage = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [stockGroups, setStockGroups] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [mainQuantity, setMainQuantity] = useState("");
    const [subQuantity, setSubQuantity] = useState("");
    const [wastageItems, setWastageItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);

    useEffect(() => {
        const fetchStockGroups = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.post(`${baseUrl}/getstocksbygroup`, {
                    token,
                    outlet_name: selectedOutlet
                });
                setStockGroups(response.data);
            } catch (error) {
                console.error('Error fetching stock groups:', error);
                toast.error('Error fetching stock items');
            }
        };

        fetchStockGroups();
    }, [token, selectedOutlet]);

    const handleAddItem = () => {
        if (!selectedItem) {
            toast.error("Please select an item");
            return;
        }

        if ((!mainQuantity || isNaN(mainQuantity) || parseFloat(mainQuantity) <= 0) &&
            (!subQuantity || isNaN(subQuantity) || parseFloat(subQuantity) <= 0)) {
            toast.error("Please enter a valid quantity in at least one field");
            return;
        }

        const newItems = [];

        if (mainQuantity && !isNaN(mainQuantity)) {
            newItems.push({
                id: selectedItem.id,
                item_name: selectedItem.name,
                quantity: parseFloat(mainQuantity),
                uom: selectedItem.uom
            });
        }

        if (subQuantity && !isNaN(subQuantity) &&
            selectedItem.unitDefinitionUOM &&
            selectedItem.unitDefinitionUOM.trim() !== '' &&
            selectedItem.uom !== selectedItem.unitDefinitionUOM) {
            newItems.push({
                id: selectedItem.id,
                item_name: selectedItem.name,
                quantity: parseFloat(subQuantity),
                uom: selectedItem.unitDefinitionUOM
            });
        }

        if (editingItemId) {
            setWastageItems(prevItems => [
                ...prevItems.filter(item => item.id !== editingItemId),
                ...newItems
            ]);
        } else {
            setWastageItems(prevItems => [...prevItems, ...newItems]);
        }

        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleEditItem = (item) => {
        const relatedItems = wastageItems.filter(i => i.id === item.id);
        const stockItem = stockGroups
            .flatMap(group => group.stocks)
            .find(stock => stock.ItemName === item.id);

        setSelectedItem({
            id: item.id,
            name: item.item_name,
            uom: stockItem?.UOM || '',
            unitDefinitionUOM: stockItem?.UnitdefinitionUOM || ''
        });

        const mainItem = relatedItems.find(i => i.uom === stockItem?.UOM);
        const subItem = relatedItems.find(i => i.uom === stockItem?.UnitdefinitionUOM);

        setMainQuantity(mainItem ? mainItem.quantity.toString() : "");
        setSubQuantity(subItem ? subItem.quantity.toString() : "");
        setEditingItemId(item.id);
    };

    const handleCancelEdit = () => {
        setSelectedItem(null);
        setMainQuantity("");
        setSubQuantity("");
        setEditingItemId(null);
    };

    const handleRemoveItem = (id) => {
        setWastageItems(wastageItems.filter(item => item.id !== id));
        if (editingItemId === id) {
            handleCancelEdit();
        }
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these wastage stocks?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet) {
                                toast.error("Please select an outlet");
                                return;
                            }
                            if (wastageItems.length === 0) {
                                toast.error("Please add at least one item");
                                return;
                            }

                            setIsLoading(true);
                            try {
                                const today = new Date().toISOString().split('T')[0];
                                const payload = {
                                    token,
                                    outlet_name: selectedOutlet,
                                    received_date: today,
                                    items: wastageItems.map(item => ({
                                        item_name: item.item_name,
                                        quantity: item.quantity,
                                        uom: item.uom
                                    }))
                                };

                                await axios.post(`${baseUrl}/wastage-items`, payload);
                                toast.success("Wastage added successfully!");
                                setWastageItems([]);
                                setEditingItemId(null);
                            } catch (error) {
                                console.error('Error submitting wastage:', error);
                                toast.error('Error submitting wastage');
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-wastage-stocks'
            }
        );
    };

    const itemOptions = stockGroups.flatMap(group =>
        group.stocks.map(stock => ({
            value: stock.ItemName,
            label: stock.ItemName,
            data: {
                id: stock.ItemName,
                name: stock.ItemName,
                uom: stock.UOM,
                unitDefinitionUOM: stock.UnitdefinitionUOM
            }
        }))
    );

    const showSubUnitInput = selectedItem &&
        selectedItem.unitDefinitionUOM &&
        selectedItem.unitDefinitionUOM.trim() !== '' &&
        selectedItem.uom.toLowerCase() !== selectedItem.unitDefinitionUOM.toLowerCase();

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-5xl my-5 mx-auto">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    {token && selectedOutlet && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-semibold mb-4">Stock Wastage</h2>

                            <div className="grid grid-cols-3 items-end gap-4 mb-6">
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                                    <Select
                                        options={itemOptions}
                                        value={selectedItem ? {
                                            value: selectedItem.id,
                                            label: selectedItem.name,
                                            data: selectedItem
                                        } : null}
                                        onChange={(selectedOption) => {
                                            setSelectedItem(selectedOption?.data || null);
                                            setMainQuantity("");
                                            setSubQuantity("");
                                        }}
                                        isSearchable
                                        placeholder="Search or select item..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div className="flex gap-4 col-span-2">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary Unit</label>
                                        <div className="flex gap-3">
                                            <div>
                                                <label className="block text-sm text-gray-700 font-medium mb-1">UOM</label>
                                                <input
                                                    type="text"
                                                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                                                    value={selectedItem?.uom || ""}
                                                    readOnly
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm text-gray-700 font-medium mb-1">Quantity</label>
                                                <input
                                                    type="number"
                                                    className="w-full p-2 border border-gray-300 rounded-md"
                                                    value={mainQuantity}
                                                    onChange={(e) => setMainQuantity(e.target.value)}
                                                    min="0"
                                                    step="any"
                                                    placeholder="Enter quantity"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {showSubUnitInput && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Unit</label>
                                            <div className="flex gap-3">
                                                <div>
                                                    <label className="block text-sm text-gray-700 font-medium mb-1">UOM</label>
                                                    <input
                                                        type="text"
                                                        className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                                                        value={selectedItem?.unitDefinitionUOM || ""}
                                                        readOnly
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm text-gray-700 font-medium mb-1">Quantity</label>
                                                    <input
                                                        type="number"
                                                        className="w-full p-2 border border-gray-300 rounded-md"
                                                        value={subQuantity}
                                                        onChange={(e) => setSubQuantity(e.target.value)}
                                                        min="0"
                                                        step="any"
                                                        placeholder="Enter quantity"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    className={`py-2 px-4 rounded-md ${editingItemId ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                    onClick={handleAddItem}
                                >
                                    {editingItemId ? "Update Item" : "Add Item"}
                                </button>
                                {editingItemId && (
                                    <button
                                        className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
                                        onClick={handleCancelEdit}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>

                            {wastageItems.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-lg font-medium mb-2">Wasted Items</h3>
                                    <div className="rounded-md border overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                                <tr>
                                                    <th></th>
                                                    <th></th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {Object.entries(
                                                    wastageItems.reduce((acc, item) => {
                                                        if (!acc[item.id]) {
                                                            acc[item.id] = {
                                                                item_name: item.item_name,
                                                                price: item.price,
                                                                mainUnit: null,
                                                                subUnit: null
                                                            };
                                                        }
                                                        const stockItem = stockGroups
                                                            .flatMap(group => group.stocks)
                                                            .find(stock => stock.ItemName === item.id);

                                                        if (item.uom === stockItem?.UOM) {
                                                            acc[item.id].mainUnit = item;
                                                        } else if (stockItem?.UnitdefinitionUOM && stockItem.UnitdefinitionUOM.trim() !== '') {
                                                            acc[item.id].subUnit = item;
                                                        }
                                                        return acc;
                                                    }, {})
                                                ).map(([id, item]) => {
                                                    const stockItem = stockGroups
                                                        .flatMap(group => group.stocks)
                                                        .find(stock => stock.ItemName === id);
                                                    const hasSubUnit = stockItem?.UnitdefinitionUOM && stockItem.UnitdefinitionUOM.trim() !== '';

                                                    return (
                                                        <tr key={id} className={editingItemId === id ? "bg-yellow-50" : ""}>
                                                            <td className="px-6 py-4 whitespace-nowrap">{item.item_name}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap">{item.price}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap">{item.mainUnit?.uom || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap">{item.mainUnit?.quantity || '-'}</td>
                                                            {hasSubUnit ? (
                                                                <>
                                                                    <td className="px-6 py-4 whitespace-nowrap">{item.subUnit?.uom || '-'}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">{item.subUnit?.quantity || '-'}</td>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td className="px-6 py-4 whitespace-nowrap">-</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">-</td>
                                                                </>
                                                            )}
                                                            <td className="px-6 py-4 whitespace-nowrap space-x-2">
                                                                <button
                                                                    className="text-blue-600 hover:text-blue-900"
                                                                    onClick={() => handleEditItem(item.mainUnit || item.subUnit)}
                                                                >
                                                                    <FaEdit className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    className="text-red-600 hover:text-red-900"
                                                                    onClick={() => handleRemoveItem(id)}
                                                                >
                                                                    <FaTrashAlt className="h-4 w-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4 flex justify-end">
                                        <button
                                            className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                            onClick={handleSubmit}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AddWastage;